import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Facebook, Send } from "lucide-react";
import EmailLink from "@/components/EmailLink";
import Seo from "@/components/Seo";

export default function Inquiries() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/inquiries`, formData);
      toast.success("Your inquiry has been sent! We'll respond within 24 hours.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-transition pt-[72px]" data-testid="inquiries-page">
      <Seo
        title="Contact & Inquiries | Fleuris Noir — Minneapolis Florist"
        description="Contact Fleuris Noir for luxury flower delivery, weddings, events and corporate florals in Minneapolis and the Twin Cities. Call (612) 547-9260 or email hello@fleurisnoir.com."
        path="/inquiries"
      />
      {/* Header */}
      <section className="section bg-[#0c0c0c] border-b border-[#b8966a]/10">
        <div className="container-custom text-center">
          <p className="overline mb-4">Get In Touch</p>
          <h1 className="font-display text-4xl md:text-5xl text-[#f8f5f0] mb-4">
            Inquiries
          </h1>
          <p className="font-body text-[#a19d98] max-w-2xl mx-auto">
            Whether you have a question about our services, want to discuss a custom project, 
            or simply wish to say hello, we'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section bg-[#161616]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl text-[#f8f5f0] mb-8">
                Contact Information
              </h2>
              
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-[#b8966a]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#b8966a]" size={18} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#f8f5f0] mb-1">Based In</h3>
                    <p className="font-body text-[#a19d98]">
                      Minneapolis, MN<br />
                      Serving the greater metro & beyond
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-[#b8966a]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#b8966a]" size={18} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#f8f5f0] mb-1">Call</h3>
                    <a href="tel:+16125479260" className="font-body text-[#a19d98] hover:text-[#b8966a] transition-colors">
                      (612) 547-9260
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-[#b8966a]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#b8966a]" size={18} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-[#f8f5f0] mb-1">Email</h3>
                    <EmailLink
                      email="hello@fleurisnoir.com"
                      className="font-body text-[#a19d98] hover:text-[#b8966a] transition-colors"
                      testId="inquiries-email"
                    >
                      hello@fleurisnoir.com
                    </EmailLink>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-heading text-lg text-[#f8f5f0] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href="https://instagram.com/fleurisnoir"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-[#b8966a]/20 flex items-center justify-center text-[#a19d98] hover:text-[#b8966a] hover:border-[#b8966a] transition-all"
                    data-testid="inquiries-instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://facebook.com/fleurisnoir"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 border border-[#b8966a]/20 flex items-center justify-center text-[#a19d98] hover:text-[#b8966a] hover:border-[#b8966a] transition-all"
                    data-testid="inquiries-facebook"
                  >
                    <Facebook size={20} />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#0c0c0c] border border-[#b8966a]/10 p-8 md:p-12">
                <h2 className="font-heading text-2xl text-[#f8f5f0] mb-8">
                  Send Us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="overline block mb-3">Your Name *</label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="overline block mb-3">Email Address *</label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="overline block mb-3">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="(612) 547-9260"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label className="overline block mb-3">Subject *</label>
                      <select
                        required
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        className="bg-transparent"
                        data-testid="contact-subject"
                      >
                        <option value="" className="bg-[#0c0c0c]">Select a subject</option>
                        <option value="General Inquiry" className="bg-[#0c0c0c]">General Inquiry</option>
                        <option value="Custom Order" className="bg-[#0c0c0c]">Custom Order</option>
                        <option value="Event Consultation" className="bg-[#0c0c0c]">Event Consultation</option>
                        <option value="Wedding Inquiry" className="bg-[#0c0c0c]">Wedding Inquiry</option>
                        <option value="Floral Bar Booking" className="bg-[#0c0c0c]">Floral Bar Booking</option>
                        <option value="Press & Media" className="bg-[#0c0c0c]">Press & Media</option>
                        <option value="Other" className="bg-[#0c0c0c]">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="overline block mb-3">Your Message *</label>
                    <textarea
                      required
                      rows={6}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your project, event, or inquiry..."
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center justify-center gap-2 w-full md:w-auto disabled:opacity-50"
                    data-testid="contact-submit"
                  >
                    {loading ? (
                      "Sending..."
                    ) : (
                      <>
                        Send Message
                        <Send size={14} />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
